import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Client } from '@app/core/models/client';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { ClientService } from '@app/core/services/client.service';
import { FeatureService } from '@app/core/services/feature.service';
import { NotificationThrottlingService } from '@app/core/services/notification-throttling.service';
import { TitleService } from '@app/core/services/title.service';
import { ScrollConstants } from '@app/core/utils/scroll-constants';
import { SessionService } from '@app/security/session.service';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { ExperienceNotificationThrottlingMetrics } from './../core/models/notification-throttling';
import { Ordering } from '@app/core/utils/ordering';
import { SortableComponent } from '@app/core/sortable-component';
import { format } from 'date-fns';
import { PageManager } from '@app/core/utils/page-manager';
import { CircleRadioBtnComponent } from '../shared/circle-radio-btn/circle-radio-btn.component';
import { SliderRangeInputComponent } from '../shared/slider-range-input/slider-range-input.component';
import { ColumnSortIconComponent } from '../shared/column-sort-icon/column-sort-icon.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { BarChartComponent } from '../shared/bar-chart/bar-chart.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../shared/message-dialog/message-dialog.component';

@Component({
  selector: 'app-notification-throttling.',
  templateUrl: './notification-throttling.component.html',
  styleUrls: ['./notification-throttling.component.scss'],
  standalone: true,
  imports: [
    CircleRadioBtnComponent,
    SliderRangeInputComponent,
    ColumnSortIconComponent,
    InfiniteScrollDirective,
    SpinnerComponent,
    BarChartComponent,
    MessageDialogComponent_1,
  ],
})
export class NotificationThrottlingComponent
  extends SortableComponent
  implements OnInit, OnDestroy
{
  @ViewChild('messageDialog') messageDialog: MessageDialogComponent;

  throttleEnabled: boolean = false;
  throttleRate: number = 20000;
  featureFlags = FeatureFlags;
  client: Client;
  experienceNotificationThrottlingMetrics: ExperienceNotificationThrottlingMetrics[] =
    [];
  isFailedToLoadMetrics: boolean = false;
  isLoadingMetrics = false;
  lastUpdatedDateStr: string;
  pageManager: PageManager = new PageManager();
  page = 1;
  pageSize = 50;
  hasMoreMetrics = true;
  readonly throttle = ScrollConstants.throttle;
  readonly scrollDistance = ScrollConstants.scrollDistance;
  ordering: Ordering;

  constructor(
    private router: Router,
    private titleService: TitleService,
    private sessionService: SessionService,
    private clientService: ClientService,
    private featureService: FeatureService,
    private notificationThrottlingService: NotificationThrottlingService,
  ) {
    super();
  }

  ngOnInit() {
    this.titleService.activate('Throttling Management');
    if (this.featureService.checkFlag(this.featureFlags.throttling)) {
      this.getClient();
    }
    this.lastUpdatedDateStr = this.formatLastUpdated();
    this.loadMetrics();
  }

  ngOnDestroy() {}

  private getClient(): void {
    this.clientService
      .getClient(this.sessionService.selectedClient.value)
      .subscribe(
        (client) => {
          this.client = client;
          if (this.client) {
            this.throttleEnabled =
              this.client.notification_throttling?.enabled || false;
            this.throttleRate =
              this.client.notification_throttling?.max_rate || 20000; // Default to 20000 if throttling is not previously set
          }
        },
        () => {
          this.client = new Client();
          this.messageDialog.showMessage(
            `Oops...Could not load client: ${this.sessionService.currentUser.clientId}`,
          );
        },
      );
  }

  handleThrottleToggle(event) {
    this.throttleEnabled = event;
  }

  onSliderChange(val: number) {
    this.throttleRate = val;
  }

  private onSuccessfulSave(): void {
    this.getClient(); // GET client config on page load
    this.messageDialog.showMessage(
      'Client global notification throttling settings saved successfully.',
    );
  }

  private onFailedSaved(err: HttpErrorResponse): void {
    const serverMessage = this.clientService.cleanseError(err);
    this.messageDialog.showMessage(
      `Oops...could not save global notification throttling settings: ${serverMessage}`,
    );
  }

  async onSave() {
    if (this.throttleRate < 2500 || this.throttleRate > 50000) {
      console.error(
        'Throttle rate must be between 2500 and 50000 milliseconds.',
      );
      return;
    }
    this.clientService
      .updateClientPartial(
        this.sessionService.selectedClient.value,
        'notification_throttling',
        {
          enabled: this.throttleEnabled,
          max_rate: this.throttleRate,
          time_unit: 'hour', // 'hour' is the default time_unit, until notification throttling by seconds, minutes or days is introduced
        },
      )
      .subscribe({
        next: (response) => this.onSuccessfulSave(),
        error: (error) => this.onFailedSaved(error),
      });
  }

  fetchData() {
    this.pageManager = new PageManager();
    this.hasMoreMetrics = true;
    this.loadMetrics();
  }

  formatLastUpdated() {
    const date = new Date();
    // Get user's local time zone abbreviation
    const tzAbbr = date
      .toLocaleTimeString('en-US', { timeZoneName: 'short' })
      .split(' ')
      .pop();
    const formattedDate = format(date, "MM/dd/yy 'at' HH:mm");
    return `${formattedDate} ${tzAbbr}`;
  }

  loadMetrics() {
    const page = this.page;
    this.isLoadingMetrics = true;
    this.notificationThrottlingService
      .getExperienceNotificationThrottlingMetrics(
        this.page,
        this.pageSize,
        this.ordering,
      )
      .subscribe({
        next: (results) => {
          results.forEach((r) => {
            Object.values(r.notificationStatus).forEach((status) => {
              if (status.timeBucket) {
                status.timeBucket = new Date(
                  status.timeBucket,
                ).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                });
              }
            });
          });
          if (results.length === 0 || results.length < this.pageSize) {
            this.hasMoreMetrics = false;
            if (page > 1 && results.length === 0) {
              this.page--;
            }
          }
          this.pageManager.addPage(page, results);
          this.experienceNotificationThrottlingMetrics =
            this.pageManager.flattenPages();
        },
        complete: () => {
          this.isLoadingMetrics = false;
          this.isFailedToLoadMetrics = false;
        },
        error: (error) => {
          this.isFailedToLoadMetrics = true;
          this.isLoadingMetrics = false;
        },
      });
  }

  onScrollDown() {
    if (!this.isLoadingMetrics && this.hasMoreMetrics) {
      this.page++;
      this.loadMetrics();
    }
  }

  onRefresh() {
    this.lastUpdatedDateStr = this.formatLastUpdated();
    this.loadMetrics();
  }

  formatName(name: string): string {
    if (name.length > 25) {
      return name.slice(0, 25) + '...';
    }
    return name;
  }

  formatMaxRate(settings: { maxRate: number; enabled: boolean }): string {
    if (settings.enabled) {
      return settings.maxRate + ' per hour';
    }
    return 'Throttling Off';
  }

  onEditMetric(metric: ExperienceNotificationThrottlingMetrics) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [
          'cx-builder',
          'message-builder',
          metric.experienceId,
          'message',
          metric.componentId,
        ],
        { queryParams: { view: 'throttling' } },
      ),
    );

    window.open(url, '_blank');
  }
}
